/**
 * reportCentre.api.ts — Reporting Centre endpoints (recon + audit + rule report families).
 * CompanyId is stamped server-side from the JWT; filters never send it.
 */
import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  SubledgerSummaryFilter, SubledgerSummaryDto,
  SubledgerDetailFilter, SubledgerDetailRow,
  ControlExceptionsFilter, ControlExceptionsDto,
  SuspenseFilter, SuspenseReportDto,
  ClearingOutstandingFilter, ClearingOutstandingDto,
  TaxGlFilter, TaxGlReconciliationDto,
  AuditTrailFilter, AuditTrailReportDto,
  JournalChangesFilter, JournalChangesReportDto,
  DeletedCancelledFilter, DeletedCancelledReportDto,
  UserActivityFilter, UserActivityReportDto,
  ApprovalHistoryFilter, ApprovalHistoryReportDto,
  ReversalRegisterFilter, ReversalRegisterReportDto,
  UnusualJournalsFilter, UnusualJournalsDto,
  SodExceptionsFilter, SodExceptionsDto,
  ReportRule, UpdateReportRule,
  PeriodStatusFilter, PeriodStatusReportDto,
  PostCloseTransactionsFilter, PostCloseTransactionRow,
  YearEndReportFilter, YearEndReportDto,
  SourceTraceFilter, SourceTraceRow,
  PostingSummaryFilter, PostingSummaryReportDto,
  FcBalancesFilter, FcBalancesReportDto,
  FxGainLossFilter, FxGainLossReportDto,
  RevaluationRegisterDto,
  FsMappingReportDto,
  OffsettingFilter, OffsettingReportDto,
  WhtRegisterFilter, WhtRegisterReportDto,
} from '@/types/reportCentre.types';

// ── Reconciliation & control reports ──────────────────────────────────────────

export const reconReportApi = {
  /** GET /api/v1/recon-reports/subledger-summary */
  getSubledgerSummary: (filter: SubledgerSummaryFilter) =>
    apiClient.get<ApiResponse<SubledgerSummaryDto>>('/recon-reports/subledger-summary', { params: filter }),

  /** GET /api/v1/recon-reports/subledger-detail — AR | AP only */
  getSubledgerDetail: (filter: SubledgerDetailFilter) =>
    apiClient.get<ApiResponse<PagedResult<SubledgerDetailRow>>>('/recon-reports/subledger-detail', { params: filter }),

  /** GET /api/v1/recon-reports/control-exceptions */
  getControlExceptions: (filter: ControlExceptionsFilter) =>
    apiClient.get<ApiResponse<ControlExceptionsDto>>('/recon-reports/control-exceptions', { params: filter }),

  /** GET /api/v1/recon-reports/suspense */
  getSuspense: (filter: SuspenseFilter) =>
    apiClient.get<ApiResponse<SuspenseReportDto>>('/recon-reports/suspense', { params: filter }),

  /** GET /api/v1/recon-reports/clearing-outstanding */
  getClearingOutstanding: (filter: ClearingOutstandingFilter) =>
    apiClient.get<ApiResponse<ClearingOutstandingDto>>('/recon-reports/clearing-outstanding', { params: filter }),

  /** GET /api/v1/recon-reports/tax-gl */
  getTaxGl: (filter: TaxGlFilter) =>
    apiClient.get<ApiResponse<TaxGlReconciliationDto>>('/recon-reports/tax-gl', { params: filter }),
};

// ── Audit & register reports ──────────────────────────────────────────────────

export const auditReportApi = {
  /** GET /api/v1/audit-reports/trail — sensitive members always null on this route */
  getAuditTrail: (filter: AuditTrailFilter) =>
    apiClient.get<ApiResponse<AuditTrailReportDto>>('/audit-reports/trail', { params: filter }),

  /** GET /api/v1/audit-reports/trail/sensitive — requires VIEW_RPT_SENSITIVE */
  getAuditTrailSensitive: (filter: AuditTrailFilter) =>
    apiClient.get<ApiResponse<AuditTrailReportDto>>('/audit-reports/trail/sensitive', { params: filter }),

  /** GET /api/v1/audit-reports/journal-changes */
  getJournalChanges: (filter: JournalChangesFilter) =>
    apiClient.get<ApiResponse<JournalChangesReportDto>>('/audit-reports/journal-changes', { params: filter }),

  /** GET /api/v1/audit-reports/deleted-cancelled */
  getDeletedCancelled: (filter: DeletedCancelledFilter) =>
    apiClient.get<ApiResponse<DeletedCancelledReportDto>>('/audit-reports/deleted-cancelled', { params: filter }),

  /** GET /api/v1/audit-reports/user-activity */
  getUserActivity: (filter: UserActivityFilter) =>
    apiClient.get<ApiResponse<UserActivityReportDto>>('/audit-reports/user-activity', { params: filter }),

  /** GET /api/v1/audit-reports/approval-history */
  getApprovalHistory: (filter: ApprovalHistoryFilter) =>
    apiClient.get<ApiResponse<ApprovalHistoryReportDto>>('/audit-reports/approval-history', { params: filter }),

  /** GET /api/v1/audit-reports/reversals */
  getReversals: (filter: ReversalRegisterFilter) =>
    apiClient.get<ApiResponse<ReversalRegisterReportDto>>('/audit-reports/reversals', { params: filter }),
};

// ── Rule-driven reports (unusual journals + SoD) ──────────────────────────────

export const ruleReportApi = {
  /** GET /api/v1/rule-reports/unusual-journals */
  getUnusualJournals: (filter: UnusualJournalsFilter) =>
    apiClient.get<ApiResponse<UnusualJournalsDto>>('/rule-reports/unusual-journals', { params: filter }),

  /** GET /api/v1/rule-reports/sod-exceptions */
  getSodExceptions: (filter: SodExceptionsFilter) =>
    apiClient.get<ApiResponse<SodExceptionsDto>>('/rule-reports/sod-exceptions', { params: filter }),

  /** GET /api/v1/rule-reports/rules?ruleType=UNUSUAL|SOD — omit for both */
  getRules: (ruleType?: string) =>
    apiClient.get<ApiResponse<ReportRule[]>>('/rule-reports/rules', {
      params: ruleType ? { ruleType } : undefined,
    }),

  /** PUT /api/v1/rule-reports/rules/{id} — editing a platform default creates a company override */
  updateRule: (id: string, dto: UpdateReportRule) =>
    apiClient.put<ApiResponse<ReportRule>>(`/rule-reports/rules/${id}`, dto),
};

// ── Period-closing / source-trace / currency / master-data reports ────────────

export const periodReportApi = {
  /** GET /api/v1/period-reports/status */
  getPeriodStatus: (filter: PeriodStatusFilter) =>
    apiClient.get<ApiResponse<PeriodStatusReportDto>>('/period-reports/status', { params: filter }),

  /** GET /api/v1/period-reports/post-close-transactions */
  getPostCloseTransactions: (filter: PostCloseTransactionsFilter) =>
    apiClient.get<ApiResponse<PagedResult<PostCloseTransactionRow>>>('/period-reports/post-close-transactions', { params: filter }),

  /** GET /api/v1/period-reports/year-end */
  getYearEnd: (filter: YearEndReportFilter) =>
    apiClient.get<ApiResponse<YearEndReportDto>>('/period-reports/year-end', { params: filter }),

  /** GET /api/v1/period-reports/source-trace */
  getSourceTrace: (filter: SourceTraceFilter) =>
    apiClient.get<ApiResponse<PagedResult<SourceTraceRow>>>('/period-reports/source-trace', { params: filter }),

  /** GET /api/v1/period-reports/posting-summary */
  getPostingSummary: (filter: PostingSummaryFilter) =>
    apiClient.get<ApiResponse<PostingSummaryReportDto>>('/period-reports/posting-summary', { params: filter }),

  /** GET /api/v1/period-reports/fc-balances */
  getFcBalances: (filter: FcBalancesFilter) =>
    apiClient.get<ApiResponse<FcBalancesReportDto>>('/period-reports/fc-balances', { params: filter }),

  /** GET /api/v1/period-reports/fx-gainloss */
  getFxGainLoss: (filter: FxGainLossFilter) =>
    apiClient.get<ApiResponse<FxGainLossReportDto>>('/period-reports/fx-gainloss', { params: filter }),

  /** GET /api/v1/period-reports/revaluation-register */
  getRevaluationRegister: () =>
    apiClient.get<ApiResponse<RevaluationRegisterDto>>('/period-reports/revaluation-register'),

  /** GET /api/v1/period-reports/fs-mapping */
  getFsMapping: () =>
    apiClient.get<ApiResponse<FsMappingReportDto>>('/period-reports/fs-mapping'),

  /** GET /api/v1/period-reports/offsetting */
  getOffsetting: (filter: OffsettingFilter) =>
    apiClient.get<ApiResponse<OffsettingReportDto>>('/period-reports/offsetting', { params: filter }),

  /** GET /api/v1/period-reports/wht-register */
  getWhtRegister: (filter: WhtRegisterFilter) =>
    apiClient.get<ApiResponse<WhtRegisterReportDto>>('/period-reports/wht-register', { params: filter }),
};
